import type { QrScanningLogRow } from '../data/qrScanningLog';
import { dateRangeColumnFilterToBounds } from '../utils/columnFilter';
import { formatDisplayDateTime } from '../utils/dateFormat';
import { caseApiRequest } from './apiClient';

export type QrScanningLogListParams = {
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'ASC' | 'DESC';
  columnFilters?: Record<string, string>;
  signal?: AbortSignal;
};

export type QrScanningLogListResult = {
  items: QrScanningLogRow[];
  total: number;
  totalPages: number;
};

type ApiQrScanningLogRow = {
  id?: string;
  event?: string;
  case_request_id?: string;
  file_ref_no?: string;
  file_category?: string;
  sub_file_category?: string;
  system_automated?: boolean;
  date_time?: string;
};

type ApiQrScanningLogPage = {
  content?: ApiQrScanningLogRow[];
  totalElements?: number;
  totalPages?: number;
};

const FILTER_FIELD_MAP: Record<string, string> = {
  event: 'event',
  fileRef: 'file_ref_no',
  fileCategory: 'file_category',
  subFileCategory: 'sub_file_category',
};

export const QR_LOG_SORT_FIELD_MAP: Record<string, string> = {
  event: 'EVENT',
  fileRef: 'FILE_REF_NO',
  fileCategory: 'FILE_CATEGORY',
  subFileCategory: 'SUB_FILE_CATEGORY',
  eventAt: 'DATE_TIME',
  systemAutomated: 'SYSTEM_AUTOMATED',
};

function adaptQrScanningLogRow(row: ApiQrScanningLogRow): QrScanningLogRow {
  const dateTime = row.date_time ?? '';
  return {
    id: row.id ?? row.case_request_id ?? `${row.file_ref_no ?? 'qr'}-${dateTime}`,
    fileRef: row.file_ref_no ?? '',
    fileCategory: row.file_category ?? '',
    subFileCategory: row.sub_file_category ?? '',
    event: row.event ?? '',
    eventAt: dateTime ? formatDisplayDateTime(dateTime) || dateTime : '',
    systemAutomated: row.system_automated ?? false,
    caseRequestId: row.case_request_id ?? '',
  };
}

export function buildQrScanningLogListBody(params: QrScanningLogListParams): Record<string, unknown> {
  const body: Record<string, unknown> = {
    sort_by: params.sortBy ?? 'DATE_TIME',
    sort_direction: params.sortDir ?? 'DESC',
  };

  for (const [column, value] of Object.entries(params.columnFilters ?? {})) {
    const trimmed = value.trim();
    if (!trimmed) continue;

    if (column === 'eventAt') {
      const bounds = dateRangeColumnFilterToBounds(trimmed);
      if (bounds.from) body.date_time_from = bounds.from;
      if (bounds.to) body.date_time_to = bounds.to;
      continue;
    }

    const apiField = FILTER_FIELD_MAP[column];
    if (apiField) body[apiField] = trimmed;
  }

  return body;
}

export async function listQrScanningLogs(
  params: QrScanningLogListParams = {},
): Promise<QrScanningLogListResult> {
  const page = params.page ?? 0;
  const pageSize = params.pageSize ?? 10;
  const result = await caseApiRequest<ApiQrScanningLogPage>(
    `/qr-scanning-logs/list?page=${page}&size=${pageSize}`,
    {
      method: 'POST',
      body: buildQrScanningLogListBody(params),
      signal: params.signal,
      headers: { applicationCode: 'INTRANET' },
    },
  );

  if (!result.ok) {
    throw new Error(result.message || 'Could not load QR scanning logs.');
  }

  const content = Array.isArray(result.data?.content) ? result.data.content : [];
  return {
    items: content.map(adaptQrScanningLogRow),
    total: result.data?.totalElements ?? content.length,
    totalPages: Math.max(1, result.data?.totalPages ?? 1),
  };
}